import { useQuery } from "@tanstack/react-query";
import { videoService } from "@/services/videoService";
import useAuth from "@/hooks/stores/useAuth";

// Hook to fetch assigned videos for admin dashboard
export const useAssignedVideos = (options = {}) => {
  const { userId, isAuthenticated } = useAuth();
  
  return useQuery({
    queryKey: ['assigned-videos', userId], // Include userId to make cache user-specific
    queryFn: videoService.getAssignedVideos,
    staleTime: 0, // Always considered stale
    refetchInterval: 4 * 60 * 1000, // Auto-call every 4 minutes
    enabled: !!userId && isAuthenticated, // Only run query when user is authenticated and has userId
    ...options
  });
};

// Hook to fetch user videos for user dashboard
export const useUserVideos = (options = {}) => {
  const { userId, isAuthenticated } = useAuth();
  
  return useQuery({
    queryKey: ['user-videos', userId], // Include userId to make cache user-specific
    queryFn: async () => {
      try {
        return await videoService.getUserVideos();
      } catch (error) {
        // Handle the specific case where user has no approved videos
        if (error?.response?.status === 404 && 
            error?.response?.data?.message === "No approved videos found for this user.") {
          // Return empty data structure instead of throwing error
          return {
            success: true,
            message: "No approved videos found for this user.",
            data: []
          };
        }
        // Re-throw other errors
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry if it's the "no videos found" 404 error
      if (error?.response?.status === 404 && 
          error?.response?.data?.message === "No approved videos found for this user.") {
        return false;
      }
      // Retry other errors up to 3 times
      return failureCount < 3;
    },
    enabled: !!userId && isAuthenticated, // Only run query when user is authenticated and has userId
    ...options
  });
}; 